<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Saldo' : 'Adicionar Saldo' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label for="bancoSelect" class="form-label">Banco</label>
          <select id="bancoSelect" v-model="form.banco" required class="form-select">
            <option disabled value="">Selecione um banco</option>
            <option v-for="banco in bankOptions" :key="banco" :value="banco">{{ banco }}</option>
          </select>
        </div>
        <div v-if="form.banco === 'Outro'">
          <label for="bancoCustom" class="form-label">Nome do Banco</label>
          <input id="bancoCustom" v-model="form.customBanco" type="text" required placeholder="Ex: Banco Neon" class="form-input" />
        </div>
        <div>
          <label for="bancoSaldo" class="form-label">Saldo (R$)</label>
          <input id="bancoSaldo" v-model.number="form.saldo" type="number" step="0.01" required placeholder="0.00" class="form-input" />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.id)
const isSubmitting = ref(false)

const form = ref({ id: null, banco: '', customBanco: '', saldo: null })

const apiUrl = 'https://backend.controlease.com.br/saldos-banco'
const authConfig = () => {
  const t = localStorage.getItem('token')
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {}
}

const bankThemes = {
  'Santander': {}, 'Nubank': {}, 'Banco do Brasil': {}, 'C6 Bank': {}, 'Itaú': {},
  'Bradesco': {}, 'Caixa': {}, 'Inter': {}, 'PagBank': {}, 'PicPay': {},
  'Mercado Pago': {}, 'Outro': {}
}

const bankOptions = computed(() => {
  return Object.keys(bankThemes).sort((a, b) => {
    if (a === 'Outro') return 1
    if (b === 'Outro') return -1
    return a.localeCompare(b)
  })
})

const fetchSaldo = async () => {
  if (!isEditing.value) return
  try {
    const { data } = await axios.get(`${apiUrl}/${route.params.id}`, authConfig())
    const isKnownBank = bankOptions.value.includes(data.banco)
    if (isKnownBank) {
      form.value = { id: data.id, banco: data.banco, customBanco: '', saldo: data.saldo }
    } else {
      form.value = { id: data.id, banco: 'Outro', customBanco: data.banco, saldo: data.saldo }
    }
  } catch {
    alert('Erro ao carregar dados do saldo.')
    router.push({ name: 'SaldoBancoUser' })
  }
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true

  const finalBankName = form.value.banco === 'Outro' ? form.value.customBanco : form.value.banco
  if (!finalBankName) {
    alert('O nome do banco é obrigatório.')
    isSubmitting.value = false
    return
  }

  const payload = { id: form.value.id, banco: finalBankName, saldo: form.value.saldo }

  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${payload.id}`, payload, authConfig())
    } else {
      await axios.post(apiUrl, payload, authConfig())
    }
    router.push({ name: 'SaldoBancoUser' })
  } catch (error) {
    alert(error.response?.data?.message || 'Erro ao salvar saldo.')
  } finally {
    isSubmitting.value = false
  }
}

const goBack = () => {
  router.push({ name: 'SaldoBancoUser' })
}

onMounted(() => {
  if (isEditing.value) fetchSaldo()
  else form.value = { id: null, banco: '', customBanco: '', saldo: null }
})
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input, .form-select { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-select { @apply appearance-none; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
